import type { EntitySchema } from '@/types/schema';
import { db } from '@/storage/sqlite';
import type { RecordStore, StoredRecord, StoredApp, StoredMember, StoredInvite } from './RecordStore';

interface MemberRow { app_id: string; user_id: string; role: string; joined_at: string | null; }
interface InviteRow { token: string; app_id: string; role: string; email: string | null; phone: string | null; invited_by: string | null; created_at: string | null; }

interface RecordRow { id: string; app_id: string; entity: string; data: string | null; created_at: string | null; updated_at: string | null; }
interface AppRow { app_id: string; name: string; description: string | null; icon: string | null; category: string | null; creator_id: string | null; published_at: string | null; definition: string | null; schemas: string | null; }
interface SchemaRow { schema: string; }
interface EnrollmentRow { app_id: string; }

function rowToRecord(row: RecordRow): StoredRecord {
  return {
    id: row.id, appId: row.app_id, entity: row.entity,
    data: row.data ? (JSON.parse(row.data) as Record<string, unknown>) : {},
    createdAt: row.created_at ?? '', updatedAt: row.updated_at ?? '',
  };
}

function rowToApp(row: AppRow): StoredApp {
  return {
    appId: row.app_id, name: row.name, description: row.description ?? '', icon: row.icon ?? 'cube',
    category: row.category ?? undefined, creatorId: row.creator_id ?? '', publishedAt: row.published_at ?? '',
    definition: row.definition ? JSON.parse(row.definition) : {}, schemas: row.schemas ? JSON.parse(row.schemas) : [],
  };
}

export class SqliteRecordStore implements RecordStore {
  async putRecord(r: StoredRecord): Promise<void> {
    await db.runAsync(
      `INSERT OR REPLACE INTO app_records (app_id, entity, id, data, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)`,
      [r.appId, r.entity, r.id, JSON.stringify(r.data), r.createdAt, r.updatedAt],
    );
  }

  async getRecord(appId: string, entity: string, id: string): Promise<StoredRecord | null> {
    const row = await db.getFirstAsync<RecordRow>(
      `SELECT * FROM app_records WHERE app_id = ? AND entity = ? AND id = ?`, [appId, entity, id],
    );
    return row ? rowToRecord(row) : null;
  }

  // TODO: cursor-based pagination not yet implemented; returns first `limit` rows.
  async listRecords(appId: string, entity: string, limit: number, _cursor?: string): Promise<{ items: StoredRecord[]; cursor?: string }> {
    const rows = await db.getAllAsync<RecordRow>(
      `SELECT * FROM app_records WHERE app_id = ? AND entity = ? ORDER BY created_at ASC LIMIT ?`, [appId, entity, limit],
    );
    return { items: rows.map(rowToRecord) };
  }

  async deleteRecord(appId: string, entity: string, id: string): Promise<void> {
    await db.runAsync(`DELETE FROM app_records WHERE app_id = ? AND entity = ? AND id = ?`, [appId, entity, id]);
  }

  async putSchema(appId: string, schema: EntitySchema): Promise<void> {
    await db.runAsync(
      `INSERT OR REPLACE INTO app_schemas (app_id, entity, schema) VALUES (?, ?, ?)`,
      [appId, schema.name, JSON.stringify(schema)],
    );
  }

  async getSchema(appId: string, entity: string): Promise<EntitySchema | null> {
    const row = await db.getFirstAsync<SchemaRow>(
      `SELECT schema FROM app_schemas WHERE app_id = ? AND entity = ?`, [appId, entity],
    );
    return row ? (JSON.parse(row.schema) as EntitySchema) : null;
  }

  async putApp(app: StoredApp): Promise<void> {
    await db.runAsync(
      `INSERT OR REPLACE INTO published_apps (app_id, name, description, icon, category, creator_id, published_at, definition, schemas)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [app.appId, app.name, app.description, app.icon, app.category ?? null, app.creatorId, app.publishedAt, JSON.stringify(app.definition), JSON.stringify(app.schemas)],
    );
  }

  async getApp(appId: string): Promise<StoredApp | null> {
    const row = await db.getFirstAsync<AppRow>(`SELECT * FROM published_apps WHERE app_id = ?`, [appId]);
    return row ? rowToApp(row) : null;
  }

  async listApps(): Promise<StoredApp[]> {
    const rows = await db.getAllAsync<AppRow>(`SELECT * FROM published_apps ORDER BY published_at DESC`);
    return rows.map(rowToApp);
  }

  async deleteApp(appId: string): Promise<void> {
    await db.runAsync(`DELETE FROM published_apps WHERE app_id = ?`, [appId]);
    await db.runAsync(`DELETE FROM app_schemas WHERE app_id = ?`, [appId]);
    await db.runAsync(`DELETE FROM app_records WHERE app_id = ?`, [appId]);
    await db.runAsync(`DELETE FROM app_members WHERE app_id = ?`, [appId]);
    await db.runAsync(`DELETE FROM app_invites WHERE app_id = ?`, [appId]);
  }

  async putMember(m: StoredMember): Promise<void> {
    await db.runAsync(`INSERT OR REPLACE INTO app_members (app_id, user_id, role, joined_at) VALUES (?, ?, ?, ?)`, [m.appId, m.userId, m.role, m.joinedAt]);
  }
  async getMember(appId: string, userId: string): Promise<StoredMember | null> {
    const r = await db.getFirstAsync<MemberRow>(`SELECT * FROM app_members WHERE app_id = ? AND user_id = ?`, [appId, userId]);
    return r ? { appId: r.app_id, userId: r.user_id, role: r.role, joinedAt: r.joined_at ?? '' } : null;
  }
  async listMembers(appId: string): Promise<StoredMember[]> {
    const rows = await db.getAllAsync<MemberRow>(`SELECT * FROM app_members WHERE app_id = ?`, [appId]);
    return rows.map((r) => ({ appId: r.app_id, userId: r.user_id, role: r.role, joinedAt: r.joined_at ?? '' }));
  }
  async removeMember(appId: string, userId: string): Promise<void> {
    await db.runAsync(`DELETE FROM app_members WHERE app_id = ? AND user_id = ?`, [appId, userId]);
  }
  async putInvite(inv: StoredInvite): Promise<void> {
    await db.runAsync(`INSERT OR REPLACE INTO app_invites (token, app_id, role, email, phone, invited_by, created_at) VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [inv.token, inv.appId, inv.role, inv.email ?? null, inv.phone ?? null, inv.invitedBy, inv.createdAt]);
  }
  async getInvite(token: string): Promise<StoredInvite | null> {
    const r = await db.getFirstAsync<InviteRow>(`SELECT * FROM app_invites WHERE token = ?`, [token]);
    return r ? { token: r.token, appId: r.app_id, role: r.role, email: r.email ?? undefined, phone: r.phone ?? undefined, invitedBy: r.invited_by ?? '', createdAt: r.created_at ?? '' } : null;
  }
  async deleteInvite(token: string): Promise<void> {
    await db.runAsync(`DELETE FROM app_invites WHERE token = ?`, [token]);
  }

  async enroll(userId: string, appId: string): Promise<void> {
    await db.runAsync(
      `INSERT OR IGNORE INTO app_enrollments (user_id, app_id, enrolled_at) VALUES (?, ?, ?)`,
      [userId, appId, new Date().toISOString()],
    );
  }

  async listEnrollments(userId: string): Promise<string[]> {
    const rows = await db.getAllAsync<EnrollmentRow>(`SELECT app_id FROM app_enrollments WHERE user_id = ?`, [userId]);
    return rows.map((r) => r.app_id);
  }
}
